import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Eye, 
  UserX, 
  UserCheck,
  Filter,
  Download,
  X,
  Save,
  AlertCircle,
  AlertTriangle,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import Card from '../shared/Card';
import SearchLogRow from '../shared/SearchLogRow';
import { SkeletonRows } from '../shared/Skeleton';
import { RecentOrdersCard } from '../shared/OrderOverview';

const SEARCHES_PAGE_SIZE = 10;

// Extract modal components outside to prevent re-creation on each render
const ViewUserModal = ({ selectedUser, onClose, showActivity }) => {
  const [searches, setSearches] = useState([]);
  const [searchCount, setSearchCount] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchesLoading, setSearchesLoading] = useState(showActivity);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(showActivity);

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const fetchSearches = async (page) => {
    setSearchesLoading(true);
    try {
      const offset = (page - 1) * SEARCHES_PAGE_SIZE;
      const { data, error, count } = await supabase
        .from('customer_search_logs')
        .select('*', { count: 'exact' })
        .eq('user_id', selectedUser.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + SEARCHES_PAGE_SIZE - 1);

      if (error) throw error;

      setSearches(data || []);
      setSearchCount(count || 0);
      setSearchPage(page);
    } catch (error) {
      console.error('Error fetching user searches:', error);
      showError(error, 'Failed to fetch user searches');
    } finally {
      setSearchesLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('id, order_number, status, internal_status, created_at, accounts:user_id ( full_name, company_name )')
        .eq('user_id', selectedUser.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching user orders:', error);
      showError(error, 'Failed to fetch user orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (showActivity) {
      fetchOrders();
      fetchSearches(1);
    }
  }, [selectedUser.id, showActivity]);

  const searchPages = Math.ceil(searchCount / SEARCHES_PAGE_SIZE);

  return (
  <div className="fixed inset-0 z-50 overflow-y-auto">
    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div className="fixed inset-0 transition-opacity" aria-hidden="true">
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              View User Details
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="w-6 h-6" aria-hidden="true" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Basic Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.email || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.full_name || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.role || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedUser?.disabled ? 'Inactive' : 'Active'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Last Login</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedUser?.last_login ? new Date(selectedUser.last_login).toLocaleString('en-AU') : 'Never'}
                </p>
              </div>
            </div>

            {/* Company Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Company Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.company_name || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">MHS Account Number</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.mhs_account_number || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">DEB ID</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.deb_id || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Created</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedUser?.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '-'}
                </p>
              </div>
            </div>

            {/* Pricing & Permissions */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Pricing & Permissions</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Discount Tier</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.discount_tier?.toUpperCase() || 'DEF'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Default Price Schedule Code</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.default_price_schedule_code || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Override Price Schedule Code</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser?.override_price_schedule_code || '-'}</p>
              </div>
            </div>

            {/* Access Controls */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Access Controls</h4>
              
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="text-sm text-gray-700">Is Admin:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {selectedUser?.is_admin ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-700">Is Staff:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {selectedUser?.is_staff ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-700">Is Company Admin:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {selectedUser?.is_company_admin ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-700">Can View BOM Components:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {selectedUser?.can_view_bom_components ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-700">Can View Full Stock:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {selectedUser?.can_view_full_stock ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {showActivity && (
            <div className="mt-6">
              <RecentOrdersCard
                orders={orders}
                loading={ordersLoading}
                orderPath={(order) => `/admin/orders?search=${encodeURIComponent(order.order_number)}`}
              />
            </div>
          )}

          {showActivity && (
            <Card
              className="mt-6"
              padding="sm"
              header={
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Recent Searches</h3>
                  <span className="text-sm text-gray-500">{searchCount} total</span>
                </div>
              }
              footer={searchPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <span className="text-sm text-gray-600">
                    Showing {(searchPage - 1) * SEARCHES_PAGE_SIZE + 1}–{Math.min(searchPage * SEARCHES_PAGE_SIZE, searchCount)} of {searchCount}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<ChevronLeft className="h-4 w-4" aria-hidden="true" />}
                      disabled={searchesLoading || searchPage === 1}
                      onClick={() => fetchSearches(searchPage - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-700 whitespace-nowrap">
                      Page {searchPage} of {searchPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={searchesLoading || searchPage === searchPages}
                      onClick={() => fetchSearches(searchPage + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              )}
            >
              {searchesLoading ? (
                <SkeletonRows rows={3} />
              ) : searches.length > 0 ? (
                <div className="space-y-2">
                  {searches.map(search => (
                    <SearchLogRow key={search.id} search={search} showUser={false} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">No searches recorded for this user</p>
              )}
            </Card>
          )}
        </div>

        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:w-auto sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
  );
};

const EditUserModal = ({ selectedUser, onClose, onUpdate, loading }) => {
  const roles = ['user', 'admin', 'staff', 'manager'];
  const discountTiers = ['def', 'r1', 'r2', 'r3', 'r4', 'r5', 'r6'];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const [editingUser, setEditingUser] = useState({
    email: selectedUser?.email || '',
    full_name: selectedUser?.full_name || '',
    role: selectedUser?.role || 'user',
    discount_tier: selectedUser?.discount_tier || 'def',
    deb_id: selectedUser?.deb_id || '',
    is_admin: selectedUser?.is_admin || false,
    company_name: selectedUser?.company_name || '',
    mhs_account_number: selectedUser?.mhs_account_number || '',
    can_view_bom_components: selectedUser?.can_view_bom_components || false,
    default_price_schedule_code: selectedUser?.default_price_schedule_code || '',
    override_price_schedule_code: selectedUser?.override_price_schedule_code || '',
    can_view_full_stock: selectedUser?.can_view_full_stock || false,
    is_staff: selectedUser?.is_staff || false,
    is_company_admin: selectedUser?.is_company_admin || false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const updates = {
      full_name: editingUser.full_name || null,
      role: editingUser.role,
      discount_tier: editingUser.discount_tier,
      deb_id: editingUser.deb_id ? parseInt(editingUser.deb_id) : null,
      is_admin: editingUser.is_admin,
      company_name: editingUser.company_name || null,
      mhs_account_number: editingUser.mhs_account_number || null,
      can_view_bom_components: editingUser.can_view_bom_components,
      default_price_schedule_code: editingUser.default_price_schedule_code || null,
      override_price_schedule_code: editingUser.override_price_schedule_code || null,
      can_view_full_stock: editingUser.can_view_full_stock,
      is_staff: editingUser.is_staff,
      is_company_admin: editingUser.is_company_admin
    };

    await onUpdate(selectedUser.id, updates);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Edit User: {selectedUser?.email}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Basic Information</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      disabled
                      value={editingUser.email}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input
                      type="text"
                      value={editingUser.full_name}
                      onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <select
                      value={editingUser.role}
                      onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    >
                      {roles.map(role => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Company Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Company Information</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <input
                      type="text"
                      value={editingUser.company_name}
                      onChange={(e) => setEditingUser({...editingUser, company_name: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">MHS Account Number</label>
                    <input
                      type="text"
                      value={editingUser.mhs_account_number}
                      onChange={(e) => setEditingUser({...editingUser, mhs_account_number: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">DEB ID</label>
                    <input
                      type="number"
                      value={editingUser.deb_id}
                      onChange={(e) => setEditingUser({...editingUser, deb_id: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>
                </div>

                {/* Pricing & Permissions */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Pricing & Permissions</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Discount Tier</label>
                    <select
                      value={editingUser.discount_tier}
                      onChange={(e) => setEditingUser({...editingUser, discount_tier: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    >
                      {discountTiers.map(tier => (
                        <option key={tier} value={tier}>
                          {tier.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default Price Schedule Code</label>
                    <input
                      type="text"
                      value={editingUser.default_price_schedule_code}
                      onChange={(e) => setEditingUser({...editingUser, default_price_schedule_code: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Override Price Schedule Code</label>
                    <input
                      type="text"
                      value={editingUser.override_price_schedule_code}
                      onChange={(e) => setEditingUser({...editingUser, override_price_schedule_code: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>
                </div>

                {/* Access Controls */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Access Controls</h4>
                  
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingUser.is_admin}
                        onChange={(e) => setEditingUser({...editingUser, is_admin: e.target.checked})}
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      <span className="ml-2 text-sm text-gray-700">Is Admin</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingUser.is_staff}
                        onChange={(e) => setEditingUser({...editingUser, is_staff: e.target.checked})}
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      <span className="ml-2 text-sm text-gray-700">Is Staff</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingUser.is_company_admin}
                        onChange={(e) => setEditingUser({...editingUser, is_company_admin: e.target.checked})}
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      <span className="ml-2 text-sm text-gray-700">Is Company Admin</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingUser.can_view_bom_components}
                        onChange={(e) => setEditingUser({...editingUser, can_view_bom_components: e.target.checked})}
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      <span className="ml-2 text-sm text-gray-700">Can View BOM Components</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingUser.can_view_full_stock}
                        onChange={(e) => setEditingUser({...editingUser, can_view_full_stock: e.target.checked})}
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      <span className="ml-2 text-sm text-gray-700">Can View Full Stock</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-black hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update User'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DeleteUserModal = ({ selectedUser, onClose, onConfirm, loading }) => {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleConfirm = async () => {
    if (confirmEmail === selectedUser.email) {
      setIsDeleting(true);
      await onConfirm(selectedUser.id);
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Delete User Account
                </h3>
                <div className="mt-4 space-y-3">
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800 font-medium flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" aria-hidden="true" />Warning: This action cannot be undone!</p>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    <p className="mb-2">You are about to permanently delete:</p>
                    <div className="bg-gray-50 rounded-md p-3 space-y-1">
                      <p><span className="font-medium text-gray-700">Email:</span> {selectedUser?.email}</p>
                      <p><span className="font-medium text-gray-700">Name:</span> {selectedUser?.full_name || 'N/A'}</p>
                      <p><span className="font-medium text-gray-700">Company:</span> {selectedUser?.company_name || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 space-y-2">
                    <p className="font-medium">This will delete:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-500">
                      <li>User account and authentication credentials</li>
                      <li>All API keys</li>
                      <li>User-specific data (search history, etc.)</li>
                    </ul>
                    
                    <p className="font-medium mt-3">This will preserve:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-500">
                      <li>Orders (made available to other company users)</li>
                    </ul>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type <span className="font-bold text-red-600">{selectedUser?.email}</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter user's email"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirmEmail !== selectedUser?.email || isDeleting || loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting || loading ? 'Deleting...' : 'Delete User'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting || loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateUserModal = ({ newUser, setNewUser, onSubmit, onClose, loading, isCompanyAdmin = false, companyDefaults = null }) => {
  const roles = isCompanyAdmin ? ['user'] : ['user', 'admin', 'staff', 'manager'];
  const discountTiers = ['def', 'r1', 'r2', 'r3', 'r4', 'r5', 'r6'];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={onSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Create New User
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>

              {/* Company Admin Info Banner */}
              {isCompanyAdmin && companyDefaults && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-800">Company Admin Mode</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        New users will be automatically assigned to your company:
                        <strong> {companyDefaults.company_name}</strong>
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Company fields are locked and will be inherited from your account.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Basic Information</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email *</label>
                    <input
                      type="email"
                      required
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input
                      type="text"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      disabled={isCompanyAdmin}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {roles.map(role => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>
                    {isCompanyAdmin && (
                      <p className="mt-1 text-xs text-gray-500">
                        Company admins can only create regular users
                      </p>
                    )}
                  </div>
                </div>

                {/* Company Information - Only visible to backend admins */}
                {!isCompanyAdmin && (
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Company Information</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Name</label>
                      <input
                        type="text"
                        value={newUser.company_name}
                        onChange={(e) => setNewUser({...newUser, company_name: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">MHS Account Number</label>
                      <input
                        type="text"
                        value={newUser.mhs_account_number}
                        onChange={(e) => setNewUser({...newUser, mhs_account_number: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">DEB ID</label>
                      <input
                        type="number"
                        value={newUser.deb_id}
                        onChange={(e) => setNewUser({...newUser, deb_id: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                      />
                    </div>
                  </div>
                )}

                {/* Pricing & Permissions - Only visible to backend admins */}
                {!isCompanyAdmin && (
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Pricing & Permissions</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Discount Tier</label>
                      <select
                        value={newUser.discount_tier}
                        onChange={(e) => setNewUser({...newUser, discount_tier: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                      >
                        {discountTiers.map(tier => (
                          <option key={tier} value={tier}>
                            {tier.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Default Price Schedule Code</label>
                      <input
                        type="text"
                        value={newUser.default_price_schedule_code}
                        onChange={(e) => setNewUser({...newUser, default_price_schedule_code: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Override Price Schedule Code</label>
                      <input
                        type="text"
                        value={newUser.override_price_schedule_code}
                        onChange={(e) => setNewUser({...newUser, override_price_schedule_code: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                      />
                    </div>
                  </div>
                )}

                {/* Access Controls - Only visible to backend admins */}
                {!isCompanyAdmin && (
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Access Controls</h4>
                    
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.is_admin}
                          onChange={(e) => setNewUser({...newUser, is_admin: e.target.checked})}
                          className="rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <span className="ml-2 text-sm text-gray-700">Is Admin</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.is_staff}
                          onChange={(e) => setNewUser({...newUser, is_staff: e.target.checked})}
                          className="rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <span className="ml-2 text-sm text-gray-700">Is Staff</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.is_company_admin}
                          onChange={(e) => setNewUser({...newUser, is_company_admin: e.target.checked})}
                          className="rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <span className="ml-2 text-sm text-gray-700">Is Company Admin</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.can_view_bom_components}
                          onChange={(e) => setNewUser({...newUser, can_view_bom_components: e.target.checked})}
                          className="rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <span className="ml-2 text-sm text-gray-700">Can View BOM Components</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.can_view_full_stock}
                          onChange={(e) => setNewUser({...newUser, can_view_full_stock: e.target.checked})}
                          className="rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <span className="ml-2 text-sm text-gray-700">Can View Full Stock</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-black hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create User'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 20;

  // Company admin state
  const [currentUser, setCurrentUser] = useState(null);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [companyDefaults, setCompanyDefaults] = useState(null);

  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    role: 'user',
    discount_tier: 'def',
    deb_id: '',
    is_admin: false,
    company_name: '',
    mhs_account_number: '',
    can_view_bom_components: false,
    default_price_schedule_code: '',
    override_price_schedule_code: '',
    can_view_full_stock: false,
    is_staff: false,
    is_company_admin: false
  });

  const roles = ['user', 'admin', 'staff', 'manager'];
  const discountTiers = ['def', 'r1', 'r2', 'r3', 'r4', 'r5', 'r6'];

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [currentPage, searchTerm, filterRole, filterStatus, currentUser]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        setCurrentUser(data);
        setIsCompanyAdmin(data.is_company_admin && !data.is_admin);
        
        if (data.is_company_admin) {
          setCompanyDefaults({
            company_name: data.company_name,
            deb_id: data.deb_id,
            mhs_account_number: data.mhs_account_number,
            discount_tier: data.discount_tier,
            default_price_schedule_code: data.default_price_schedule_code
          });
        }
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      showError(error, 'Failed to fetch user details');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('accounts')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      // Filter by company for company admins
      if (isCompanyAdmin && currentUser) {
        query = query
          .eq('company_name', currentUser.company_name)
          .eq('deb_id', currentUser.deb_id);
      }

      if (searchTerm) {
        query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,company_name.ilike.%${searchTerm}%`);
      }

      if (filterRole) {
        query = query.eq('role', filterRole);
      }

      if (filterStatus) {
        const isDisabled = filterStatus === 'inactive';
        query = query.eq('disabled', isDisabled);
      }

      // Apply pagination
      query = query.range((currentPage - 1) * usersPerPage, currentPage * usersPerPage - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      setUsers(data || []);
      setTotalUsers(count || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      showError(error, 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.email) {
      showError(new Error('Email is required'), 'Email is required');
      return;
    }

    try {
      setLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found');
      }

      // Prepare request body
      const requestBody = {
        email: newUser.email,
        full_name: newUser.full_name || null,
        role: newUser.role || 'user',
        discount_tier: newUser.discount_tier || 'def',
        can_view_bom_components: newUser.can_view_bom_components || false,
        default_price_schedule_code: newUser.default_price_schedule_code || null,
        override_price_schedule_code: newUser.override_price_schedule_code || null,
        can_view_full_stock: newUser.can_view_full_stock || false
      };

      // System admins include full data
      if (!isCompanyAdmin) {
        requestBody.deb_id = newUser.deb_id || null;
        requestBody.is_admin = newUser.is_admin || false;
        requestBody.company_name = newUser.company_name || null;
        requestBody.mhs_account_number = newUser.mhs_account_number || null;
        requestBody.is_staff = newUser.is_staff || false;
        requestBody.is_company_admin = newUser.is_company_admin || false;
      }

      // Choose appropriate endpoint
      const functionName = isCompanyAdmin ? 'create-company-user' : 'create-user';

      console.log(`Creating user via ${functionName}:`, requestBody);

      const { data, error } = await supabase.functions.invoke(functionName, {
        body: requestBody,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      // Log full response for debugging
      console.log('Edge function response:', { data, error });

      // Check for invocation error first
      if (error) {
        console.error('Edge function invocation error:', error);
        
        // Try to extract detailed error message from various possible locations
        let errorMessage = 'Failed to call user creation function';
        
        if (error.context?.body?.error) {
          errorMessage = error.context.body.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
      }

      // Check if response indicates failure
      if (!data || !data.success) {
        const errorMessage = data?.error || 'User creation failed - no error details provided';
        console.error('Edge function returned error:', errorMessage);
        console.error('Detailed error:', data);
        throw new Error(errorMessage);
      }

      // Show success message
      showNotification(data.message || `User created successfully!`, 'success');
      
      // Reset form with company defaults if company admin
      setNewUser({
        email: '',
        full_name: '',
        role: 'user',
        discount_tier: companyDefaults?.discount_tier || 'def',
        deb_id: '',
        is_admin: false,
        company_name: '',
        mhs_account_number: '',
        can_view_bom_components: false,
        default_price_schedule_code: companyDefaults?.default_price_schedule_code || '',
        override_price_schedule_code: '',
        can_view_full_stock: false,
        is_staff: false,
        is_company_admin: false
      });
      
      // Wait for user list to refresh before closing modal
      await fetchUsers();
      
      // Small delay to ensure user sees the new user in the list
      setTimeout(() => {
        setShowCreateModal(false);
      }, 500);

    } catch (error) {
      console.error('Error creating user:', error);
      showError(error, 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const { data, error } = await supabase
        .from('accounts')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      showNotification('User updated successfully', 'success');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      showError(error, 'Failed to update user');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found');
      }

      console.log('Toggling user status:', { userId: user.id, disabled: !user.disabled });

      const { data, error } = await supabase.functions.invoke('toggle-user-status', {
        body: {
          userId: user.id,
          disabled: !user.disabled
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      console.log('Toggle user status response:', { data, error });

      if (error) {
        console.error('Edge function invocation error:', error);
        const errorMessage = 
          error?.context?.body?.error || 
          error?.message || 
          'Failed to call user status toggle function';
        throw new Error(errorMessage);
      }

      if (!data || !data.success) {
        const errorMessage = data?.error || 'User status toggle failed - no error details provided';
        console.error('Edge function returned error:', errorMessage);
        throw new Error(errorMessage);
      }

      showNotification(
        data.message || `User ${user.disabled ? 'activated' : 'deactivated'} successfully`, 
        'success'
      );
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      console.error('Detailed error:', error);
      showError(error, 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found');
      }

      console.log('Deleting user:', userId);

      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      console.log('Delete user response:', { data, error });

      if (error) {
        console.error('Edge function invocation error:', error);
        const errorMessage = 
          error?.context?.body?.error || 
          error?.message || 
          'Failed to call user deletion function';
        throw new Error(errorMessage);
      }

      if (!data || !data.success) {
        const errorMessage = data?.error || 'User deletion failed - no error details provided';
        console.error('Edge function returned error:', errorMessage);
        throw new Error(errorMessage);
      }

      showNotification(data.message || 'User deleted successfully', 'success');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showError(error, 'Failed to delete user');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = !filterRole || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description={isCompanyAdmin
          ? `Manage user accounts for ${currentUser?.company_name || 'your company'}`
          : 'Manage user accounts and permissions'}
        actions={
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            icon={<Plus className="h-4 w-4" />}
          >
            Create User
          </Button>
        }
      />

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
          >
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                {!isCompanyAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount Tier
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    {!isCompanyAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowViewModal(true);
                          }}
                          className="ml-4 text-left group"
                        >
                          <div className="text-sm font-medium text-gray-900 group-hover:text-indigo-600 group-hover:underline">
                            {user.full_name || 'No Name'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.company_name || '-'}</div>
                      <div className="text-sm text-gray-500">{user.mhs_account_number || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.is_admin ? 'bg-red-100 text-red-800' :
                        user.is_staff ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                      </span>
                    </td>
                    {!isCompanyAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.discount_tier?.toUpperCase() || 'DEF'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {user.disabled ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowViewModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="View User"
                        aria-label="View User"
                      >
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowEditModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit User"
                        aria-label="Edit User"
                      >
                        <Edit className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleToggleUserStatus(user)}
                        className={user.disabled ? 'text-green-600 hover:text-green-900' : 'text-orange-600 hover:text-orange-900'}
                        title={user.disabled ? 'Activate User' : 'Deactivate User'}
                        aria-label={user.disabled ? 'Activate User' : 'Deactivate User'}
                      >
                        {user.disabled ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete User"
                        aria-label="Delete User"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage * usersPerPage >= totalUsers}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * usersPerPage + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(currentPage * usersPerPage, totalUsers)}
              </span>{' '}
              of <span className="font-medium">{totalUsers}</span> results
            </p>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateUserModal
          newUser={newUser}
          setNewUser={setNewUser}
          onSubmit={handleCreateUser}
          onClose={() => setShowCreateModal(false)}
          loading={loading}
          isCompanyAdmin={isCompanyAdmin}
          companyDefaults={companyDefaults}
        />
      )}
      {showViewModal && selectedUser && (
        <ViewUserModal
          selectedUser={selectedUser}
          showActivity={!isCompanyAdmin}
          onClose={() => {
            setShowViewModal(false);
            setSelectedUser(null);
          }}
        />
      )}
      {showEditModal && selectedUser && (
        <EditUserModal
          selectedUser={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onUpdate={handleUpdateUser}
          loading={loading}
        />
      )}
      {showDeleteModal && selectedUser && (
        <DeleteUserModal
          selectedUser={selectedUser}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
          onConfirm={handleDeleteUser}
          loading={loading}
        />
      )}
    </div>
  );
};

export default UserManagement;
